import React, {useEffect, useState} from "react";
import {useForm} from "react-hook-form";
import {useEyeconxStore} from "../../store/eyeconxStore";
import login from "../../services/auth.service";
import {useNavigate} from "react-router-dom";
import {motion} from "framer-motion";
import "./login.css";

const LOGO_URL = "https://eyeconx.s3.us-east-2.amazonaws.com/3conx/assets/3f6f63b82cf94f13985e104afd45f226.png";

const BRAND_FEATURES = [
    {icon: "bi-hospital", text: "Manage clinics and patient operations"},
    {icon: "bi-megaphone", text: "Run campaigns and communications"},
    {icon: "bi-shield-check", text: "Secure admin-only access"},
];

function Login() {
    const {
        register,
        handleSubmit,
        formState: { errors },
        setValue
    } = useForm();
    const [showPassword, setShowPassword] = useState(false);
    const [message, setMessage] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [rememberMe, setRememberMe] = useState(false);
    const setToken = useEyeconxStore((state) => state.setToken);
    const setUser = useEyeconxStore((state) => state.setUser);
    const setIsAuthenticate = useEyeconxStore((state) => state.setIsAuthenticate);

    const token = useEyeconxStore((state) => state.token);
    const isAuthenticated = useEyeconxStore((state) => state.isAuthenticated);
    const user = useEyeconxStore((state) => state.user);
    const navigate = useNavigate();

    useEffect(() => {
        const rememberedCredentials = localStorage.getItem('rememberedCredentials');
        if (rememberedCredentials) {
            const { username, password } = JSON.parse(rememberedCredentials);
            setValue('username', username);
            setValue('password', password);
            setRememberMe(true);
        }
    }, [setValue]);

    const onSubmit = async (data) => {
        setMessage(null);
        setIsLoading(true);
        try {
            const res = await login(data.username, data.password);

            if (res.data.statusCode === 200) {
                const user = {
                    ID: res.data.uId,
                    name: res.data.username,
                    role: res.data.role,
                    clinicId: res.data.clinicId,
                    authorization: "Bearer " + res.data.token,
                };

                setToken(res.data.token);
                setIsAuthenticate(true);
                setUser(user);

                if (rememberMe) {
                    localStorage.setItem('rememberedCredentials', JSON.stringify({
                        username: data.username,
                        password: data.password
                    }));
                } else {
                    localStorage.removeItem('rememberedCredentials');
                }
            }
        } catch (error) {
            if (!error?.response) {
                setMessage("No Server Response");
            } else if (error.response?.status === 400) {
                setMessage("Missing Username or Password");
            } else if (error.response?.status === 401 || error.response?.status === 403) {
                setMessage("Incorrect Username or Password");
            } else {
                setMessage("Login Failed. Please try again");
            }
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (isAuthenticated && token != null) {
            if (user.role === "ADMIN") {
                navigate("/dashboard");
            } else {
                setMessage("This is only for Admin access!");
                setIsAuthenticate(false);
                setUser(null);
                setToken(null);
            }
        }
    }, [isAuthenticated, token, user, navigate, setIsAuthenticate, setUser, setToken]);

    return (
        <div className="login-page">
            <aside className="login-page__brand">
                <div className="login-page__brand-bg">
                    <div className="login-page__brand-grid" />
                </div>
                <motion.div
                    className="login-page__brand-content"
                    initial={{opacity: 0, x: -24}}
                    animate={{opacity: 1, x: 0}}
                    transition={{duration: 0.5, ease: "easeOut"}}
                >
                    <img
                        src={LOGO_URL}
                        alt="EyeconX"
                        className="login-page__logo"
                    />
                    <h1 className="login-page__brand-title">
                        Super Admin Portal
                    </h1>
                    <p className="login-page__brand-subtitle">
                        Your central hub for managing clinics, campaigns, templates, and patient communications across the EyeconX platform.
                    </p>
                    <ul className="login-page__features">
                        {BRAND_FEATURES.map((feature) => (
                            <li key={feature.icon} className="login-page__feature">
                                <span className="login-page__feature-icon">
                                    <i className={`bi ${feature.icon}`} />
                                </span>
                                {feature.text}
                            </li>
                        ))}
                    </ul>
                </motion.div>
            </aside>

            <main className="login-page__form-panel">
                <motion.div
                    className="login-page__card"
                    initial={{opacity: 0, y: 20}}
                    animate={{opacity: 1, y: 0}}
                    transition={{duration: 0.45, ease: "easeOut", delay: 0.1}}
                >
                    <img
                        src={LOGO_URL}
                        alt="EyeconX"
                        className="login-page__form-logo"
                    />

                    <div className="login-page__header">
                        <h2 className="login-page__title">Welcome back</h2>
                        <p className="login-page__subtitle">
                            Sign in to your admin account to continue
                        </p>
                    </div>

                    {message && (
                        <div className="login-page__alert" role="alert">
                            <i className="bi bi-exclamation-circle-fill" />
                            <span>{message}</span>
                        </div>
                    )}

                    <form onSubmit={handleSubmit(onSubmit)} noValidate>
                        <div className="login-page__field">
                            <label htmlFor="username" className="login-page__label">
                                Username
                            </label>
                            <div className="login-page__input-wrap">
                                <i className="bi bi-person login-page__input-icon" />
                                <input
                                    type="text"
                                    id="username"
                                    className={`login-page__input ${errors.username ? 'login-page__input--invalid' : ''}`}
                                    {...register("username", { required: true })}
                                    placeholder="Enter your username"
                                    autoComplete="username"
                                    tabIndex={1}
                                />
                            </div>
                            {errors.username && (
                                <div className="login-page__error">
                                    <i className="bi bi-exclamation-circle" />
                                    Username is required
                                </div>
                            )}
                        </div>

                        <div className="login-page__field">
                            <label htmlFor="password" className="login-page__label">
                                Password
                            </label>
                            <div className="login-page__input-wrap">
                                <i className="bi bi-lock login-page__input-icon" />
                                <input
                                    type={showPassword ? "text" : "password"}
                                    id="password"
                                    className={`login-page__input login-page__input--password ${errors.password ? 'login-page__input--invalid' : ''}`}
                                    {...register("password", { required: true })}
                                    placeholder="Enter your password"
                                    autoComplete="current-password"
                                    tabIndex={2}
                                />
                                <button
                                    type="button"
                                    className="login-page__toggle-password"
                                    onClick={() => setShowPassword(!showPassword)}
                                    aria-label={showPassword ? "Hide password" : "Show password"}
                                    tabIndex={-1}
                                >
                                    <i className={`bi ${showPassword ? "bi-eye-slash" : "bi-eye"}`} />
                                </button>
                            </div>
                            {errors.password && (
                                <div className="login-page__error">
                                    <i className="bi bi-exclamation-circle" />
                                    Password is required
                                </div>
                            )}
                        </div>

                        <div className="login-page__options">
                            <label className="login-page__checkbox" htmlFor="rememberMe">
                                <input
                                    type="checkbox"
                                    id="rememberMe"
                                    checked={rememberMe}
                                    onChange={(e) => setRememberMe(e.target.checked)}
                                />
                                <span className="login-page__checkbox-label">Remember me</span>
                            </label>
                        </div>

                        <button
                            type="submit"
                            className="login-page__submit"
                            disabled={isLoading}
                            tabIndex={3}
                        >
                            {isLoading ? (
                                <>
                                    <span className="login-page__spinner" role="status" aria-hidden="true" />
                                    Signing in...
                                </>
                            ) : (
                                <>
                                    <i className="bi bi-box-arrow-in-right" />
                                    Sign in
                                </>
                            )}
                        </button>
                    </form>

                    <div className="login-page__footer-wrap">
                        <p className="login-page__footer">
                            Need access?{" "}
                            <a href="mailto:support@eyeconx.net">Contact support</a>
                        </p>
                        <span className="login-page__badge">
                            <i className="bi bi-shield-lock" />
                            Admin only
                        </span>
                    </div>
                </motion.div>
            </main>
        </div>
    );
}

export default Login;
